import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { ChevronLeft, Users, Lock, MapPin, Camera, Search, Trash2, Mail, Plus, CreditCardIcon, PauseCircle, PlayCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom'
import { useGetEventQuery, useEventInvitationListQuery, useAddNewEventInvitation, useCancelEventInvitation, useAddNewEventInvitationBulk, useEventRecordingListQuery } from "../../../hooks/useEvents"
import { useStore } from '@tanstack/react-store';
import { setShowTour, themeStore } from '../../../stores/theme';
import { noData } from '../../ui/media';
import { useQueryClient } from '@tanstack/react-query';
import { useSingleSubmit } from '../../../hooks/useSingleSubmit';
import { Tour } from 'antd';
import Modal from '../../common/ModalComponent';
import toast from 'react-hot-toast';
import SingleSubmitForm from '../../common/SingleSubmitForm';
import eventIcon from "../../../assets/icons/calendar.png"
import moment from "moment"

const EventDetails = () => {
    const { isDarkMode, isShowTour } = useStore(themeStore)
    const [activeTab, setActiveTab] = useState('Invited Users');
    const [searchRecordingTerm, setSearchRecordingTerm] = useState('');
    const [debouncedSearchRecordingTerm, setDebouncedSearchRecordingTerm] = useState("");
    const [searchInviteTerm, setSearchInviteTerm] = useState('');
    const [tourOpen, setTourOpen] = useState(true);
    const [showTourCheckbox, setShowTourCheckbox] = useState(false);
    const [steps, setSteps] = useState([]);
    const { _id } = useParams()
    const { canSubmit, resetSubmit } = useSingleSubmit();
    const { data: eventResponse, isSuccess: isEventSuccess } = useGetEventQuery(`?event_id=${_id}`, 'event')
    const [eventData, setEventData] = useState(null)
    const [recordings, setRecordings] = useState([])
    const [invitedUsers, setInvitedUsers] = useState([])
    const { REACT_APP_IMAGE_URL, REACT_APP_EXCEL_FORMAT_LINK } = process.env
    const [eventUserName, setEventUserName] = useState("")
    const [eventUserEmail, setEventUserEmail] = useState("")
    const [bulkInviteFile, setBulkInviteFile] = useState(null)
    const [showEventSendInviteModal, setShowEventSendInviteModal] = useState(false);
    const [showCancelInviteModal, setShowCancelInviteModal] = useState(false);
    const [showEventSendBulkInviteModal, setShowEventSendBulkInviteModal] = useState(false);
    const [currentEventInvitation, setCurrentEventInvitation] = useState(null);

    // Invitation
    const addNewEventInvitationMutation = useAddNewEventInvitation()
    const cancelEventInvitationMutation = useCancelEventInvitation()
    const addNewEventBulkInvitationMutation = useAddNewEventInvitationBulk()
    const navigate = useNavigate()
    const queryClient = useQueryClient();
    const recordingRef = useRef()
    const inviteUserRef = useRef()
    const videoRefs = useRef([]);
    const { data: recordingResponse, isSuccess: isRecordingSuccess } = useEventRecordingListQuery(
        activeTab === 'Recordings' ?
            `?event_id=${_id}&search_key=${debouncedSearchRecordingTerm}`
            : null,
        {
            enabled: !!activeTab
        }
    );

    const { data: invitationResponse, isSuccess: isInvitationSuccess } = useEventInvitationListQuery(
        activeTab === 'Invited Users' ?
            `?event_id=${_id}`
            : null,
        {
            enabled: !!activeTab
        }
    );

    const colors = {
        text: isDarkMode ? '#e0e0e0' : '#333333',
        border: isDarkMode ? '#333333' : '#e0e0e0',
        inputBackground: isDarkMode ? '#2d2d2d' : '#ffffff'
    };

    const resetStates = () => {
        setEventUserName("")
        setEventUserEmail("")
    }

    // invitation Methods
    const handleEventSendInvite = () => {
        if (eventUserName === "") {
            toast.error("Name is not allowed to empty")
            return
        }
        if (eventUserEmail === "") {
            toast.error("Email is not allowed to empty")
            return
        }
        const inputData = {
            email: eventUserEmail,
            name: eventUserName,
            event_id: _id
        }
        addNewEventInvitationMutation.mutate(inputData, {
            onSuccess: () => {
                resetStates();
                setShowEventSendInviteModal(false)
                toast.success("Inviation Sent Successfully")
                queryClient.invalidateQueries({ queryKey: ['invitation_list'] });
            },
            onError: (err) => {
                console.log("error", err)
                const message = err.response?.data?.message || "Send Invite Action Failed"
                toast.error(message)
            }
        })
    }

    const confirmCancelInvitation = () => {
        const inputData = {
            invitation_id: currentEventInvitation?._id
        }
        cancelEventInvitationMutation.mutate(inputData, {
            onSuccess: () => {
                setShowCancelInviteModal(false);
                toast.success("Invitation cancelled successfully");
                queryClient.invalidateQueries(['invitation_list']); // Adjust key if needed
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Failed to cancel invitation";
                toast.error(message);
            },
        });
        setShowCancelInviteModal(false);
    }

    const handleCancelInvitation = (invitation) => {
        setCurrentEventInvitation(invitation);
        setShowCancelInviteModal(true);
    };

    const handleEventSendInviteBulk = () => {
        if (!canSubmit()) return;
        if (!bulkInviteFile) {
            toast.error("Excel file must be required")
            resetSubmit();
            return
        }
        const eventBulkInviteFormData = new FormData();
        eventBulkInviteFormData.append('invitation_xlsx', bulkInviteFile)
        eventBulkInviteFormData.append('event_id', _id)

        addNewEventBulkInvitationMutation.mutate(eventBulkInviteFormData, {
            onSuccess: () => {
                resetStates();
                resetSubmit();
                setShowEventSendBulkInviteModal(false)
                toast.success("Inviation Sent Successfully")
                queryClient.invalidateQueries({ queryKey: ['invitation_list'] });
            },
            onError: (err) => {
                resetSubmit();
                const message = err.response?.data?.message || "Send Bulk Invite Action Failed"
                toast.error(message)
            }
        })
    }

    const handlePlay = (index) => {
        videoRefs.current.forEach((videoRef, i) => {
            const video = videoRef?.current;
            if (i !== index && video && !video.paused) {
                video.pause();
            }
        });
    };

    // Recording Methods
    const renderTabContent = () => {
        switch (activeTab) {
            case 'Invited Users':
                return (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-6">
                            <div className="relative flex-1 w-full">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchInviteTerm}
                                    onChange={(e) => setSearchInviteTerm(e.target.value)}
                                    className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                            </div>
                            <button
                                onClick={() => setShowEventSendBulkInviteModal(true)}
                                className="ml-4 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                                Send Bulk Invite
                            </button>
                            <button
                                onClick={() => setShowEventSendInviteModal(true)}
                                className="ml-4 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                                Send Invite
                            </button>
                        </div>
                        {invitedUsers?.length > 0 ?
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {invitedUsers.filter((invite) =>
                                    invite.name.toLowerCase().includes(searchInviteTerm.toLowerCase()) ||
                                    invite.email.toLowerCase().includes(searchInviteTerm.toLowerCase())
                                ).map((tenant) => (
                                    <div key={tenant.id} className={`${isDarkMode ? "bg-gray-900" : "bg-white"} rounded-lg p-4 border border-gray-200`}>
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                                    <Users className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"} `}>{tenant.name}</h3>
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        <Mail className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"} `} />
                                                        <span className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"} `}>{tenant.email}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleCancelInvitation(tenant)}
                                                className="text-red-500 hover:text-red-700 transition-colors">
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            :
                            <div className={`w-[100%] text-center py-16 ${isDarkMode ? "bg-[rgba(0,0,0,0.5)]" : "bg-[rgba(255,255,255,1)]"}`}>
                                <div className="flex justify-center items-center pt-8 mb-8">
                                    <img
                                        alt={"nodata"}
                                        className='w-[250px]'
                                        src={noData} />
                                </div>
                                <p className={`text-2xl ${isDarkMode ? "text-white" : "text-gray-800"} text-center`}>NO INVITATION FOUND</p>
                                <div className='w-full flex items-center justify-center py-3'>
                                    <button
                                        onClick={() => setShowEventSendInviteModal(true)}
                                        className={` justify-center py-2 w-[200px] rounded-full border text-center ${isDarkMode ? "border-white text-white" : "border-blue-600 text-blue-600"} flex items-center mt-8`}>
                                        <Plus size={18} className="mr-1" />
                                        Send Invite
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                );

            case 'Recordings':
                return (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-6">
                            <div className="relative flex-1 w-full">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchRecordingTerm}
                                    onChange={(e) => setSearchRecordingTerm(e.target.value)}
                                    className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                            </div>
                        </div>
                        {recordings?.length > 0 ?
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {recordings?.map((recording, index) => {
                                    if (!videoRefs.current[index]) {
                                        videoRefs.current[index] = React.createRef();
                                    }
                                    return (
                                        <div key={recording._id} className={`${isDarkMode ? "bg-gray-900" : "bg-white"} rounded-lg border border-gray-200 overflow-hidden shadow-sm`}>
                                            <div className="w-full self-center relative text-center">
                                                <VideoPlayer
                                                    key={recording?._id}
                                                    ref={videoRefs.current[index]}
                                                    src={`${REACT_APP_IMAGE_URL}/${recording?.files[1]?.fileName}`}
                                                    onPlay={() => handlePlay(index)}
                                                />
                                            </div>
                                            <div className="p-4">
                                                <div className="flex items-center space-x-3 mb-3">
                                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                                        {recording.user_data[0]?.profile_pic ?
                                                            recording.user_data[0]?.profile_pic?.indexOf("https") >= 0 ?
                                                                <img
                                                                    alt={"recording"}
                                                                    className='w-8 h-8 rounded'
                                                                    src={`${recording.user_data[0]?.profile_pic}`}
                                                                /> :
                                                                <img
                                                                    alt={"nodata"}
                                                                    className='w-8 h-8 rounded-full'
                                                                    src={`${REACT_APP_IMAGE_URL}/${recording.user_data[0]?.profile_pic}`}
                                                                />
                                                            :
                                                            <span className="text-xs text-white font-medium">{recording.user_data[0]?.user_name[0]}</span>}
                                                    </div>
                                                    <span className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{`${recording.user_data[0]?.user_name} (${recording.user_data[0]?.anonymous_name ?? ""})`}</span>
                                                </div>
                                                <div className={`space-y-1 text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                                                    <div>
                                                        <strong>Date:</strong> {moment(recording?.started_at).format("MM/DD/YYYY")}<br />
                                                        <strong>Start Time:</strong> ({moment(recording?.started_at).format("hh:mm:ss A")})<br />
                                                        <strong>End Time:</strong> ({moment(recording?.stopped_at).format("hh:mm:ss A")})
                                                    </div>
                                                    <div>
                                                        <strong>Start Location:</strong> {recording?.start_location?.name}<br />
                                                        [{recording?.start_location?.coordinates.join(", ")}]
                                                    </div>
                                                    <div>
                                                        <strong>End Location</strong> {recording.end_location?.name}<br />
                                                        [{recording?.end_location?.coordinates.join(", ")}]
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            :
                            <div className={`w-[100%] text-center py-16 ${isDarkMode ? "bg-[rgba(0,0,0,0.5)]" : "bg-[rgba(255,255,255,1)]"}`}>
                                <div className="flex justify-center items-center pt-8 mb-8">
                                    <img
                                        alt={"nodata"}
                                        className='w-[250px]'
                                        src={noData} />
                                </div>
                                <p className={`text-2xl ${isDarkMode ? "text-white" : "text-gray-800"} text-center`}>NO RECORDING FOUND</p>
                            </div>
                        }
                    </div>
                );
            default:
                return null;
        }
    };

    const handleExcelChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBulkInviteFile(file)
        }
    };

    const finishTour = () => {
        if (showTourCheckbox) {
            setShowTour(!showTourCheckbox)
        }
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchRecordingTerm(searchRecordingTerm);
        }, 300); // debounce delay in ms
        return () => clearTimeout(handler);
    }, [searchRecordingTerm]);

    // Event Data
    useEffect(() => {
        if (isEventSuccess) {
            setEventData(eventResponse?.data?.data)
        }
    }, [eventResponse, isEventSuccess])

    // Recordings
    useEffect(() => {
        if (isRecordingSuccess) {
            setRecordings(recordingResponse?.data?.data?.result)
        }
    }, [recordingResponse, isRecordingSuccess])

    // invitations
    useEffect(() => {
        if (isInvitationSuccess) {
            setInvitedUsers(invitationResponse?.data?.data?.result)
        }
    }, [invitationResponse, isInvitationSuccess])

    useEffect(() => {
        let temp = [{
            title: 'Invited Users',
            description: 'Invite user to watch this event',
            target: () => inviteUserRef?.current,
        }, {
            title: 'Recording',
            description: 'See the recording of this event',
            target: () => recordingRef?.current,
        }]
        setSteps(temp)
    }, [eventData])

    return (
        <>
            <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-white"}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex mb-6">
                            <button onClick={() => navigate(-1)} className="mr-4 p-2 border hover:bg-gray-100 rounded-lg transition-colors">
                                <ChevronLeft className={`w-6 h-6 ${isDarkMode ? "text-white" : "text-gray-600"} `} />
                            </button>
                            <p
                                className={`w-full text-3xl poppins-regular ${isDarkMode ? "text-white" : "text-gray-900"} mt-1`}
                                style={{ textAlign: "center" }}>EVENT DETAILS
                            </p>
                        </div>
                        {/* Event Info Card */}
                        <div className={` ${isDarkMode ? "bg-gray-900 border-gray-500" : "bg-white border-gray-200"} rounded-lg shadow-sm border-2 p-6`}>
                            <div className="flex items-start space-x-6">
                                {/* Event Image */}
                                <div className="w-[150px] h-[150px] rounded-lg relative overflow-hidden">
                                    <div className="absolute inset-0">
                                        <div className="w-full h-full">
                                            <img
                                                src={eventData?.media ? `${REACT_APP_IMAGE_URL}/${eventData?.media}` : eventIcon}
                                                alt={eventData?.name}
                                                style={eventData?.media === "" ? { background: "white" } : { background: "none" }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Event Details Grid */}
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-6">
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Event Name</p>
                                            <div className="flex items-center space-x-3 space-y-2">
                                                <Users className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{eventData?.name}</span>
                                            </div>
                                        </div>
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Users Limit</p>
                                            <div className="flex items-center space-x-1 mt-1">
                                                <Users className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{eventData?.user_limit}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Access Code</p>
                                            <div className="flex items-center space-x-1 mt-1">
                                                <Lock className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>
                                                    {(eventData?.payment_status === 2 || eventData?.payment_status === 5) ? eventData?.access_code : "******"}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Event Address</p>
                                            <div className="flex items-center space-x-1 mt-1">
                                                <MapPin className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{eventData?.location?.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Event Date</p>
                                            <div className="flex items-center space-x-1 mt-1">
                                                <Camera className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{eventData?.date}</span>
                                            </div>
                                        </div>
                                        <div className="items-center">
                                            <p style={{ color: colors.text }} className='text-[16px]'>Payment Status</p>
                                            <div className="flex items-center space-x-1 mt-1">
                                                <CreditCardIcon className="w-5 h-5 text-blue-500" />
                                                <span className={`${isDarkMode ? "text-white" : "text-gray-600"} text-[15px]`}>{eventData?.payment_status === 2 ? "Paid" : eventData?.payment_status === 5 ? "Free Trial" : "Rejected"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className="mb-8">
                        <div className={`flex space-x-1 ${isDarkMode ? "bg-gray-900 border-gray-500" : "bg-white border-gray-100"} border-2 p-1 rounded-lg w-full`}>
                            {['Invited Users', 'Recordings'].map((tab) => (
                                <button
                                    ref={tab === "Invited Users" ? inviteUserRef : recordingRef}
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`w-full px-6 py-3 rounded-lg poppins-regular transition-all ${activeTab === tab
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : `${isDarkMode ? "text-white" : "text-gray-800"} hover:text-gray-900 hover:bg-gray-200`
                                        }`}>
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Tab Content */}
                    <div className={`${isDarkMode ? "bg-gray-900" : "bg-white"} rounded-lg shadow-sm border border-gray-200 p-6`}>
                        {renderTabContent()}
                    </div>
                </div>
            </div>
            {/* Send Event Invite Modal */}
            <Modal isDarkMode={isDarkMode} key={"sendeventinviteform"} title="SEND INVITE" isOpen={showEventSendInviteModal} onClose={() => setShowEventSendInviteModal(false)}>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Name</label>
                    <input
                        type="text"
                        value={eventUserName}
                        placeholder="Name"
                        className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => setEventUserName(e.target.value)}
                        style={{
                            backgroundColor: colors.inputBackground,
                            borderColor: colors.border,
                            color: colors.text
                        }}
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Email</label>
                    <input
                        type="email"
                        value={eventUserEmail}
                        placeholder="Email"
                        className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => setEventUserEmail(e.target.value)}
                        style={{
                            backgroundColor: colors.inputBackground,
                            borderColor: colors.border,
                            color: colors.text
                        }}
                    />
                </div>
                <div className='text-center'>
                    <button
                        onClick={handleEventSendInvite}
                        disabled={addNewEventInvitationMutation?.isLoading ? true : false}
                        className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                        {addNewEventInvitationMutation?.isLoading ? "Processing..." : "Send"}
                    </button>
                </div>
            </Modal>
            {/* Cancel Event Invite Modal */}
            <Modal isDarkMode={isDarkMode} key={"cancelinvite"} title="CANCEL INVITE" isOpen={showCancelInviteModal} onClose={() => setShowCancelInviteModal(false)}>
                <div className="text-center mb-6">
                    <p className="mb-6" style={{ color: colors.text }}>Are you sure you want to cancel this user invitation?</p>
                    <div className='text-center mt-5'>
                        <button
                            onClick={() => setShowCancelInviteModal(false)}
                            className="w-[200px] py-3 text-center font-medium transition rounded-full"
                            style={{ color: colors.text }}>
                            Cancel
                        </button>
                        <button
                            onClick={confirmCancelInvitation}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mb-3 ml-3">
                            Confirm
                        </button>
                    </div>
                </div>
            </Modal>
            {/* Send Event Bulk Invite Modal */}
            <Modal isDarkMode={isDarkMode} key={"sendeventbulkinviteform"} title="SEND BULK INVITE" isOpen={showEventSendBulkInviteModal} onClose={() => setShowEventSendBulkInviteModal(false)}>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Download Excel Format File</label>
                    <a
                        href={REACT_APP_EXCEL_FORMAT_LINK}
                        download={"ExcelFormat.xlsx"}>
                        <button
                            className="px-3 py-1 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition">
                            Download
                        </button>
                    </a>
                </div>
                <SingleSubmitForm onSubmit={handleEventSendInviteBulk}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Excel File</label>
                        <input
                            className={`${isDarkMode ? "text-white" : "text gray-900"}`}
                            type='file'
                            style={{ cursor: 'pointer' }}
                            onChange={handleExcelChange}
                        />
                    </div>
                    <div className='text-center'>
                        <button
                            type='submit'
                            disabled={addNewEventBulkInvitationMutation?.isLoading ? true : false}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                            {addNewEventBulkInvitationMutation?.isLoading ? "Processing..." : "Upload Now"}
                        </button>
                    </div>
                </SingleSubmitForm>
            </Modal>
            {isShowTour && <Tour
                open={tourOpen}
                onClose={() => setTourOpen(false)}
                onFinish={() => finishTour()}
                steps={steps}
                actionsRender={(originNode, { current, total }) => (
                    <>
                        {current === total - 1 && (
                            <label>
                                <input type='checkbox' value={showTourCheckbox} onChange={(e) => setShowTourCheckbox(e.target.checked)} /> &nbsp;
                                <span>Don't show this again</span>
                            </label>
                        )}
                        <br /><br />
                        {originNode}
                    </>
                )}
            />}
        </>
    );
};


const VideoPlayer = forwardRef(({ src, onPlay }, ref) => {
    const [isPlaying, setIsPlaying] = useState(false);

    const togglePlay = () => {
        console.log("ref.current", ref?.current)
        if (!isPlaying) {
            ref.current.play();
            setIsPlaying(true);
            onPlay(); // notify parent that this video is playing
        } else {
            ref.current.pause();
            setIsPlaying(false);
        }
    };

    return (
        <div style={{ marginBottom: '30px', position: "relative", background: "#efefef" }}>
            <div style={{position: 'absolute', left: 20, bottom: 0, zIndex: 9 }}>
                <button onClick={togglePlay}>
                    {isPlaying ? (
                        <PauseCircle className="text-4xl" size={50} />
                    ) : (
                        <PlayCircle className="text-4xl" size={50} />
                    )}
                </button>
            </div>
            <video
                className='w-full h-[300px]'
                ref={ref}
                src={src}
                onContextMenu={(e) => e.preventDefault()}
                onPause={() => setIsPlaying(false)}
            />
            
        </div>
    );
});

export default EventDetails;